import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { AlertCircle, CheckCircle, BarChart3, Map } from 'lucide-react'
import AiEChartsViz from './AiEChartsViz'
import AiLeafletViz from './AiLeafletViz'
import { useData } from '../../contexts/DataContext'
import {
  processDataSpec,
  checkVisualizationCompleteness,
  createMarkersFromFacilities,
  createBarChartFromAggregated,
} from '../../utils/dataSpecProcessor'
import { applyChartDefaults } from '../../utils/aiChartDefaults'

/**
 * SmartVisualization Component
 *
 * Intelligently renders visualizations by:
 * 1. Checking if AI-generated visualization is complete
 * 2. If incomplete, uses dataSpec to create complete visualization from CSV data
 * 3. Shows data completeness indicators to users
 * 4. Always displays visualizations (no collapse/expand)
 */
export default function SmartVisualization({ response }) {
  const { loadFacilityData, facilities } = useData()
  const [enhancedVisualizations, setEnhancedVisualizations] = useState(null)
  const [completenessInfo, setCompletenessInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const visualizations = response?.visualizations || []
  const dataSpec = response?.dataSpec
  const dataCompleteness = response?.dataCompleteness

  useEffect(() => {
    enhanceVisualizations()
  }, [response])

  const enhanceVisualizations = async () => {
    if (!visualizations || visualizations.length === 0) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      const enhanced = []
      const completeness = []

      for (const viz of visualizations) {
        // Check completeness
        const check = checkVisualizationCompleteness(
          viz,
          dataCompleteness,
          response
        )
        completeness.push(check)

        // If complete or no dataSpec, use AI visualization as-is
        if (check.isComplete || !dataSpec) {
          enhanced.push({
            ...viz,
            config: applyChartDefaults(viz.config),
            enhanced: false,
            completenessCheck: check,
          })
          continue
        }

        // Incomplete - enhance with dataSpec
        try {
          const enhancedViz = await enhanceVisualizationWithDataSpec(
            viz,
            dataSpec,
            response
          )

          if (enhancedViz) {
            // Successfully enhanced - update completeness check
            const updatedCheck = {
              ...check,
              isComplete: true,
              reason: 'Enhanced with complete dataset',
            }
            enhanced.push({
              ...enhancedViz,
              enhanced: true,
              completenessCheck: updatedCheck,
              originalTitle: viz.title,
            })
          } else {
            // Enhancement failed, use original
            enhanced.push({
              ...viz,
              enhanced: false,
              completenessCheck: check,
            })
          }
        } catch (err) {
          console.error('Error enhancing visualization:', err)
          // Use original on error
          enhanced.push({
            ...viz,
            enhanced: false,
            completenessCheck: check,
          })
        }
      }

      setEnhancedVisualizations(enhanced)
      setCompletenessInfo(completeness)
    } catch (err) {
      console.error('Error in enhanceVisualizations:', err)
      setError('Failed to enhance visualizations')
      // Fallback to original visualizations
      setEnhancedVisualizations(
        visualizations.map(v => ({ ...v, enhanced: false }))
      )
    } finally {
      setLoading(false)
    }
  }

  const enhanceVisualizationWithDataSpec = async (viz, dataSpec, response) => {
    // Load CSV data (cached by DataContext)
    // Determine category from dataSpec source or response
    const category = dataSpec.source?.includes('health')
      ? 'health'
      : 'education'

    /* eslint-disable no-console */
    console.log('🔧 Enhancing visualization:', {
      vizType: viz.type,
      category,
      hasDataSpec: !!dataSpec,
    })
    /* eslint-enable no-console */

    // Ensure facilities data is loaded
    await loadFacilityData(category)
    const csvData = facilities[category]

    if (!csvData || csvData.length === 0) {
      console.warn('No CSV data available for enhancement')
      return null
    }

    /* eslint-disable no-console */
    console.log('✅ CSV data loaded:', csvData.length, 'records')
    /* eslint-enable no-console */

    // Process dataSpec
    const processed = processDataSpec(dataSpec, csvData)

    if (!processed) {
      console.warn('Failed to process dataSpec')
      return null
    }

    // Enhance based on visualization type
    if (viz.type === 'leaflet') {
      return enhanceLeafletMap(viz, processed, response)
    } else if (viz.type === 'echarts') {
      return enhanceEChart(viz, processed, response)
    }

    return null
  }

  const enhanceLeafletMap = (viz, processed, _response) => {
    const facilities = processed.facilities || []

    /* eslint-disable no-console */
    console.log('🗺️  Enhancing Leaflet map:', {
      facilitiesCount: facilities.length,
      processedType: processed.type,
    })
    /* eslint-enable no-console */

    if (facilities.length === 0) {
      console.warn('No facilities found after processing dataSpec')
      return null
    }

    // Create complete markers
    const markers = createMarkersFromFacilities(facilities, {
      nameField: 'name',
      latField: 'latitude',
      lngField: 'longitude',
      popupFields: ['district', 'subcounty'],
      popupTemplate: facility => `
        <div class="font-sans">
          <div class="font-bold text-base mb-1">${facility.name || facility.facility_name || 'Unknown'}</div>
          ${facility.district ? `<div class="text-sm text-gray-600">District: ${facility.district}</div>` : ''}
          ${facility.subcounty ? `<div class="text-sm text-gray-600">Subcounty: ${facility.subcounty}</div>` : ''}
          ${facility.ownership ? `<div class="text-sm text-gray-600">Ownership: ${facility.ownership}</div>` : ''}
        </div>
      `,
    })

    /* eslint-disable no-console */
    console.log('📍 Created markers:', markers.length)
    /* eslint-enable no-console */

    if (markers.length === 0) {
      console.warn('No valid markers created (missing coordinates)')
      return null
    }

    // Use original config as base, replace markers
    const enhanced = {
      title: viz.title,
      type: 'leaflet',
      config: {
        ...viz.config,
        markers: markers,
      },
    }

    /* eslint-disable no-console */
    console.log('✅ Enhanced map config:', {
      hasCenter: !!enhanced.config.center,
      center: enhanced.config.center,
      markersCount: enhanced.config.markers.length,
    })
    /* eslint-enable no-console */

    return enhanced
  }

  const enhanceEChart = (viz, processed, _response) => {
    if (processed.type === 'comparison' && processed.aggregated?.length > 0) {
      // Create complete bar chart
      const completeChart = createBarChartFromAggregated(processed.aggregated, {
        title: viz.title,
        seriesName: processed.aggregated[0]?.metric || 'Count',
      })

      // Merge with original config styling, apply defaults
      const mergedConfig = {
        ...viz.config,
        ...completeChart,
        xAxis: {
          ...viz.config.xAxis,
          ...completeChart.xAxis,
        },
        yAxis: {
          ...viz.config.yAxis,
          ...completeChart.yAxis,
        },
        series: completeChart.series.map((newSeries, idx) => ({
          ...viz.config.series?.[idx],
          ...newSeries,
        })),
      }

      return {
        title: viz.title,
        type: 'echarts',
        config: applyChartDefaults(mergedConfig),
      }
    }

    // For non-comparison charts, use original
    return null
  }

  if (!visualizations || visualizations.length === 0) {
    return null
  }

  const vizsToRender =
    enhancedVisualizations ||
    visualizations.map(v => ({ ...v, enhanced: false }))

  // Debug: Log what we're rendering
  /* eslint-disable no-console */
  console.log('🎨 SmartVisualization rendering:', {
    vizsCount: vizsToRender.length,
    vizs: vizsToRender.map(v => ({
      type: v.type,
      enhanced: v.enhanced,
      markersCount: v.config?.markers?.length,
      hasCenter: !!v.config?.center,
      center: v.config?.center,
    })),
  })
  /* eslint-enable no-console */

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              {visualizations.some(v => v.type === 'echarts') ? (
                <BarChart3 className="w-5 h-5 text-blue-600" />
              ) : (
                <Map className="w-5 h-5 text-green-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                Visualizations ({visualizations.length})
              </h3>
              <p className="text-xs text-gray-600 uppercase">
                {visualizations.filter(v => v.type === 'echarts').length} chart
                {visualizations.filter(v => v.type === 'echarts').length !== 1
                  ? 's'
                  : ''}
                {' • '}
                {`${visualizations.filter(v => v.type === 'leaflet').length} map${visualizations.filter(v => v.type === 'leaflet').length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          {/* Completeness indicator */}
          {completenessInfo && completenessInfo.length > 0 && (
            <div className="text-sm">
              {completenessInfo.every(c => c.isComplete) ? (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium capitalize">Complete data</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium capitalize">
                    Complete data loaded
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="px-6 py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p className="text-sm text-gray-600">
            Enhancing visualizations with complete data...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Visualizations Content - Always Visible */}
      {!loading && (
        <div className="p-6 space-y-6">
          {vizsToRender.map((viz, index) => (
            <div key={index} className="space-y-3">
              {/* Visualization Title with Enhancement Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {viz.type === 'echarts' ? (
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                  ) : (
                    <Map className="w-4 h-4 text-green-600" />
                  )}
                  <h4 className="font-medium text-gray-900">{viz.title}</h4>
                </div>

                {/* Enhancement badge */}
                {viz.enhanced && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-medium capitalize">
                    Complete dataset
                  </span>
                )}
              </div>

              {/* Completeness info - only show if not complete and not enhanced */}
              {viz.completenessCheck &&
                !viz.completenessCheck.isComplete &&
                !viz.enhanced && (
                  <div className="text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded-lg">
                    <span className="font-medium">Note:</span>{' '}
                    {viz.completenessCheck.reason}. Displaying available data.
                  </div>
                )}

              {/* Render Visualization */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {viz.type === 'echarts' ? (
                  <AiEChartsViz config={viz.config} title={viz.title} />
                ) : viz.type === 'leaflet' ? (
                  <AiLeafletViz config={viz.config} title={viz.title} />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Unsupported visualization type: {viz.type}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

SmartVisualization.propTypes = {
  response: PropTypes.shape({
    type: PropTypes.string,
    visualizations: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        type: PropTypes.oneOf(['echarts', 'leaflet']).isRequired,
        config: PropTypes.object.isRequired,
      })
    ),
    dataSpec: PropTypes.object,
    dataCompleteness: PropTypes.shape({
      category: PropTypes.string,
      totalFacilities: PropTypes.number,
      facilitiesWithCoordinates: PropTypes.number,
      missingCoordinates: PropTypes.number,
      coordinateCoveragePercent: PropTypes.number,
    }),
    rankings: PropTypes.array,
    facilities: PropTypes.array,
  }).isRequired,
}
